import React, { useEffect, useState } from "react";
import {
    Users,
    Settings,
    Cpu,
    HardDrive,
    Monitor,
    Building,
    Hash,
    Calendar,
    Clock,
    Activity,
    Globe,
    XCircle,
    CheckCircle,
    Package,
    User,
    Shield,
    Lock,
    Power,
    Info
} from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";

const ExpandableValue = ({ value, isMono = false }: { value: string, isMono?: boolean }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const textRef = React.useRef<HTMLSpanElement>(null);

    React.useEffect(() => {
        const checkTruncation = () => {
            if (textRef.current) {
                // If expanded, we don't know if it WOULD be truncated if collapsed, 
                // but we know it was truncated before expanding.
                // We mainly check when collapsed.
                if (!isExpanded) {
                    setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
                }
            }
        };

        checkTruncation();
        window.addEventListener('resize', checkTruncation);
        return () => window.removeEventListener('resize', checkTruncation);
    }, [value, isExpanded]);

    if (!value || value === "-" || value === "-") return <span className="text-slate-400 dark:text-white">-</span>;

    const strValue = String(value);

    return (
        <div className="flex flex-col items-end max-w-[65%] min-w-0">
            <span
                ref={textRef}
                className={`text-slate-900 dark:text-white text-right ${isMono ? 'font-mono' : ''} ${isExpanded ? 'break-words break-all' : 'truncate w-full'}`}
                title={strValue}
            >
                {strValue}
            </span>
            {(isTruncated || isExpanded) && (
                <button
                    onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
                    className="text-[9px] font-bold text-orange-600 dark:text-orange-500 hover:text-orange-700 dark:hover:text-orange-400 mt-0.5 transition-colors bg-orange-500/10 hover:bg-orange-500/20 px-1.5 py-0.5 rounded cursor-pointer"
                >
                    {isExpanded ? 'Show Less' : 'Show More'}
                </button>
            )}
        </div>
    );
};

interface OverviewProps {
    assetInfo: any;
    handleTabChange: (tab: any) => void
}

const Overview: React.FC<OverviewProps> = ({ assetInfo, handleTabChange }) => {


    const [biosData, setBiosData] = useState<any>(null);
    const [biosLoading, setBiosLoading] = useState(true);

    const [tpmData, setTpmData] = useState<any>(null);
    const [tpmLoading, setTpmLoading] = useState(true);

    const [showRebootModal, setShowRebootModal] = useState(false);

    let reasons: Record<string, string> = {};
    if (assetInfo.reboot_required_reasons) {
        if (typeof assetInfo.reboot_required_reasons === 'string') {
            try {
                reasons = JSON.parse(assetInfo.reboot_required_reasons);
            } catch {
                reasons = {};
            }
        } else if (typeof assetInfo.reboot_required_reasons === 'object') {
            reasons = assetInfo.reboot_required_reasons;
        }
    }

    // Extract IDs for API call
    const {
        agent_id,
        organization_id,
        asset_id,
        client_id,
        platform
    } = assetInfo;

    const isLinux = platform?.toLowerCase().includes("linux") || assetInfo?.os_name?.toLowerCase().includes("linux");

    // Fetch asset issues, BIOS, and TPM on mount
    useEffect(() => {
        const fetchIssuesBiosAndTpm = async () => {
            if (!asset_id || !organization_id || !client_id) return;

            const queryObj: any = {
                client_id,
                organization_id,
                asset_id,
            };
            if (agent_id) {
                queryObj.agent_id = agent_id;
            }

            const params = {
                query: JSON.stringify(queryObj),
            };

            // Fetch Issues

            // Fetch BIOS
            try {
                const response = await baseapi.get("/asset_bios", { params });
                if (response.data.status && response.data.data && response.data.data.length > 0) {
                    setBiosData(response.data.data[0]);
                }
            } catch (err: any) {
                showError(err.message);
            } finally {
                setBiosLoading(false);
            }

            // Fetch TPM (Only if platform is Windows)
            if (platform && platform.toLowerCase() === "windows") {
                console.log("Fetching TPM data");
                try {
                    const response = await baseapi.get("/asset_tpm", { params });
                    if (response.data.status && response.data.data && response.data.data.length > 0) {
                        setTpmData(response.data.data[0]);
                    }
                } catch (err: any) {
                    console.error("Failed to fetch TPM data", err);
                } finally {
                    setTpmLoading(false);
                }
            } else {
                setTpmLoading(false); // Skip if not windows
            }
        };

        fetchIssuesBiosAndTpm();
    }, [agent_id, asset_id, organization_id, client_id, platform]);

    // Summary cards
    const summaryCards = [
        {
            label: "Users",
            value: assetInfo.users ?? 10,
            icon: Users,
            color: "text-cyan-600 dark:text-cyan-400",
            tab: "users",
        },
        {
            label: "Services",
            value: assetInfo.services ?? 5,
            icon: Settings,
            color: "text-blue-600 dark:text-blue-400",
            tab: "services",
        },
        {
            label: "Apps",
            value: assetInfo.applications ?? 43,
            icon: Package,
            color: "text-purple-600 dark:text-purple-400",
            tab: "software",
        }
    ];

    // System Information
    const systemInformation = {
        cpu_core: assetInfo.cpu_core ?? "-",
        physical_memory: assetInfo.physical_memory ?? "-",
        hardware_model: assetInfo.hardware_model ?? "-",
        manufacturer: assetInfo.manufacturer ?? "-",
        bios_version: assetInfo.bios_version ?? "-",
        serial_number: assetInfo.serial_number ?? "-",
        os_name: assetInfo.os_name ?? "-",
        os_build: assetInfo.os_build ?? "-",
        architecture: assetInfo.architecture ?? "-",
        os_install_date: assetInfo.os_install_date ?? "-",
        timezone: assetInfo.timezone ?? "-",
        logged_in_user: assetInfo.logged_in_user ?? "-",
        uptime_days: assetInfo.uptime_days ?? "-",
        ram_percentage: assetInfo.ram_percentage ?? 0,
        memory_percentage: assetInfo.memory_percentage ?? 0,
        storage_percentage: assetInfo.storage_percentage ?? 0,
        domain_info: assetInfo.domain_info ?? "-",
        is_reboot_required: assetInfo.is_reboot_required ?? false,
    };

    // Format epoch date for BIOS
    const formatBiosDate = (epoch: string | null | undefined) => {
        if (!epoch || epoch === "0" || epoch === "-") return "-";
        const num = Number(epoch);
        if (isNaN(num)) return epoch;
        // Check if epoch is in seconds or ms. BIOS dates are often YYYYMMDD in WMI or seconds.
        const date = num > 9999999999 ? new Date(num) : new Date(num * 1000);
        return date.toLocaleDateString();
    };

    return (
        <div className="p-2.5">
            <div className="bg-white dark:bg-gray-800 rounded-md p-3 border border-slate-200 dark:border-gray-600 shadow-sm dark:shadow-none">
                {/* Header: Summary cards */}
                <div className="flex items-center justify-between mb-3">
                    <div className="flex gap-2">
                        <div className="bg-orange-600 p-1.5 text-white rounded shadow-sm">
                            <Info className="w-4 h-4"/>
                        </div>
                        <div>
                            <p className="text-slate-900 dark:text-white font-semibold text-lg">System Information</p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-4">
                        {summaryCards.map((card) => {
                            const Icon = card.icon;
                            return (
                                <button
                                    key={card.label}
                                    onClick={() => handleTabChange(card.tab)}
                                    className="flex items-center space-x-1.5 hover:opacity-80 transition-all duration-200 cursor-pointer bg-slate-50 hover:bg-slate-100 dark:bg-gray-900/50 dark:hover:bg-gray-900/80 px-2 py-1.5 rounded border border-slate-200 dark:border-gray-700/50 shadow-sm dark:shadow-none"
                                >
                                    <Icon className={`w-3.5 h-3.5 ${card.color}`} />
                                    <span className="text-[11px] text-slate-600 dark:text-gray-200">{card.label}:</span>
                                    <span className="text-slate-900 dark:text-white text-xs font-semibold">{card.value}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* System Information section */}
                <div className="bg-slate-50/70 dark:bg-gray-900 rounded-md mt-3 border border-slate-200 dark:border-gray-700/30">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 p-3">
                        {/* Hardware Information */}
                        <div className="px-1">
                            <div className="space-y-1.5 text-xs">
                                {/* CPU Cores */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                        <Cpu className="w-3 h-3 text-orange-600" />
                                        <span className="text-slate-600 dark:text-gray-200">CPU Cores:</span>
                                    </div>
                                    <span className="text-slate-900 dark:text-white font-mono">{systemInformation.cpu_core}</span>
                                </div>
                                {/* Memory */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                        <HardDrive className="w-3 h-3 text-orange-600" />
                                        <span className="text-slate-600 dark:text-gray-200">Memory:</span>
                                    </div>
                                    <span className="text-slate-900 dark:text-white font-mono">{systemInformation.physical_memory}GB</span>
                                </div>
                                {/* Model */}
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center space-x-2 mt-0.5">
                                        <Monitor className="w-3 h-3 text-orange-600" />
                                        <span className="text-slate-600 dark:text-gray-200">Model:</span>
                                    </div>
                                    <ExpandableValue value={systemInformation.hardware_model} />
                                </div>
                                {/* Manufacturer */}
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center space-x-2 mt-0.5">
                                        <Building className="w-3 h-3 text-orange-600" />
                                        <span className="text-slate-600 dark:text-gray-200">Manufacturer:</span>
                                    </div>
                                    <ExpandableValue value={systemInformation.manufacturer} />
                                </div>
                                {/* Serial */}
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center space-x-2 mt-0.5">
                                        <Hash className="w-3 h-3 text-orange-600" />
                                        <span className="text-slate-600 dark:text-gray-200">Serial:</span>
                                    </div>
                                    <ExpandableValue value={systemInformation.serial_number} isMono={true} />
                                </div>

                                <div className="flex items-start justify-between">
                                    <div className="flex items-center space-x-2 mt-0.5">
                                        <Monitor className="w-3 h-3 text-orange-600" />
                                        <span className="text-slate-600 dark:text-gray-200">OS Name:</span>
                                    </div>
                                    <ExpandableValue value={systemInformation.os_name} />
                                </div>
                            </div>
                        </div>

                        {/* Operating System Information */}
                        <div className="px-1">
                            <div className="space-y-1.5 text-xs">

                                <div className="flex items-start justify-between">
                                    <div className="flex items-center space-x-2 mt-0.5">
                                        <Settings className="w-3 h-3 text-orange-600" />
                                        <span className="text-slate-600 dark:text-gray-200">Build:</span>
                                    </div>
                                    <ExpandableValue value={systemInformation.os_build} isMono={true} />
                                </div>
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center space-x-2 mt-0.5">
                                        <Cpu className="w-3 h-3 text-orange-600" />
                                        <span className="text-slate-600 dark:text-gray-200">Architecture:</span>
                                    </div>
                                    <span className="text-slate-900 dark:text-white">{systemInformation.architecture}</span>
                                </div>
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center space-x-2 mt-0.5">
                                        <Calendar className="w-3 h-3 text-orange-600" />
                                        <span className="text-slate-600 dark:text-gray-200">Install Date:</span>
                                    </div>
                                    <ExpandableValue value={systemInformation.os_install_date} />
                                </div>
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center space-x-2 mt-0.5">
                                        <Clock className="w-3 h-3 text-orange-600" />
                                        <span className="text-slate-600 dark:text-gray-200">Timezone:</span>
                                    </div>
                                    <ExpandableValue value={systemInformation.timezone} />
                                </div>
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center space-x-2 mt-0.5">
                                        <User className="w-3 h-3 text-orange-600" />
                                        <span className="text-slate-600 dark:text-gray-200">Logged User:</span>
                                    </div>
                                    <ExpandableValue value={systemInformation.logged_in_user
                                        ? (systemInformation.logged_in_user.includes('\\')
                                            ? systemInformation.logged_in_user.split('\\')[1]
                                            : systemInformation.logged_in_user)
                                        : "-"} />
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                        <Clock className="w-3 h-3 text-orange-600" />
                                        <span className="text-slate-600 dark:text-gray-200">Uptime:</span>
                                    </div>
                                    <span className="text-slate-900 dark:text-white font-mono">{Math.floor(systemInformation.uptime_days)}d</span>
                                </div>
                            </div>
                        </div>

                        {/* System Status & Performance */}
                        <div className="px-1">
                            <div className="space-y-1.5 text-xs">

                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                        <Activity className="w-3 h-3 text-orange-600" />
                                        <span className="text-slate-600 dark:text-gray-200">CPU Usage:</span>
                                    </div>
                                    <span className={`font-semibold px-1.5 py-0.5 rounded border text-[10px] leading-none ${parseFloat((systemInformation.ram_percentage ?? 0).toString()) > 80
                                        ? 'text-red-600 dark:text-red-400 border-red-200 dark:border-red-400/50 bg-red-50 dark:bg-red-400/10'
                                        : parseFloat((systemInformation.ram_percentage ?? 0).toString()) > 60
                                            ? 'text-yellow-600 dark:text-yellow-400 border-yellow-200 dark:border-yellow-400/50 bg-yellow-50 dark:bg-yellow-400/10'
                                            : 'text-green-600 dark:text-green-400 border-green-200 dark:border-green-400/50 bg-green-50 dark:bg-green-400/10'
                                        }`}>
                                        {systemInformation.ram_percentage ?? '0'}%
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                        <HardDrive className="w-3 h-3 text-orange-600" />
                                        <span className="text-slate-600 dark:text-gray-200">Memory Usage:</span>
                                    </div>
                                    <span className={`font-semibold px-1.5 py-0.5 rounded border text-[10px] leading-none ${parseFloat((systemInformation.memory_percentage ?? 0).toString()) > 80
                                        ? 'text-red-600 dark:text-red-400 border-red-200 dark:border-red-400/50 bg-red-50 dark:bg-red-400/10'
                                        : parseFloat((systemInformation.memory_percentage ?? 0).toString()) > 60
                                            ? 'text-yellow-600 dark:text-yellow-400 border-yellow-200 dark:border-yellow-400/50 bg-yellow-50 dark:bg-yellow-400/10'
                                            : 'text-green-600 dark:text-green-400 border-green-200 dark:border-green-400/50 bg-green-50 dark:bg-green-400/10'
                                        }`}>
                                        {systemInformation.memory_percentage ?? '0'}%
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                        <HardDrive className="w-3 h-3 text-orange-600" />
                                        <span className="text-slate-600 dark:text-gray-200">Disk Usage:</span>
                                    </div>
                                    <span className={`font-semibold px-1.5 py-0.5 rounded border text-[10px] leading-none ${parseFloat((systemInformation.storage_percentage ?? 0).toString()) > 90
                                        ? 'text-red-600 dark:text-red-400 border-red-200 dark:border-red-400/50 bg-red-50 dark:bg-red-400/10'
                                        : parseFloat((systemInformation.storage_percentage ?? 0).toString()) > 80
                                            ? 'text-yellow-600 dark:text-yellow-400 border-yellow-200 dark:border-yellow-400/50 bg-yellow-50 dark:bg-yellow-400/10'
                                            : 'text-green-600 dark:text-green-400 border-green-200 dark:border-green-400/50 bg-green-50 dark:bg-green-400/10'
                                        }`}>
                                        {systemInformation.storage_percentage ?? '0'}%
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                        <Globe className="w-3 h-3 text-orange-600" />
                                        <span className="text-slate-600 dark:text-gray-200">Domain:</span>
                                    </div>
                                    <span className="text-slate-900 dark:text-white" title={systemInformation.domain_info}>{systemInformation.domain_info}</span>
                                </div>
                                {systemInformation.is_reboot_required !== null &&
                                    <div className={`flex items-center justify-between ${systemInformation.is_reboot_required == true ? 'border p-2 border-red-500 animate-pulse bg-red-50 dark:bg-red-950/20 rounded' : ''} `}>
                                        <div className="flex items-center space-x-2">
                                            <Power className="w-3 h-3 text-orange-600" />
                                            <span className="text-slate-600 dark:text-gray-200 flex items-center gap-1.5">
                                                Reboot Required:
                                                {systemInformation.is_reboot_required == true && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowRebootModal(true)}
                                                        className="text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 transition-colors p-0.5 focus:outline-none cursor-pointer flex items-center"
                                                        title="View reboot reasons"
                                                    >
                                                        <Info className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                            </span>
                                        </div>
                                        <span className={`${systemInformation.is_reboot_required == true ? 'text-red-600 dark:text-red-500 animate-pulse font-semibold' : 'text-green-600 dark:text-green-500 font-semibold'}`}>{systemInformation.is_reboot_required ? 'Yes' : 'No'}</span>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>

                {/* BIOS Details Section */}
                {!biosLoading && biosData && (
                    <div className="bg-slate-50/70 dark:bg-gray-900 rounded-md mt-4 border border-slate-200 dark:border-gray-700/30">
                        <div className="p-3">
                            <h3 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center mb-3">
                                <Cpu className="w-4 h-4 mr-1.5 text-orange-500" />
                                BIOS Details
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                                <div className="bg-white dark:bg-gray-800/50 p-2.5 rounded border border-slate-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                                    <div className="text-slate-500 dark:text-gray-400 text-[10px] uppercase tracking-wider mb-1">Manufacturer</div>
                                    <div className="text-slate-900 dark:text-white font-medium truncate" title={biosData.manufacturer}>{biosData.manufacturer || "-"}</div>
                                </div>
                                {!isLinux && (
                                    <div className="bg-white dark:bg-gray-800/50 p-2.5 rounded border border-slate-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                                        <div className="text-slate-500 dark:text-gray-400 text-[10px] uppercase tracking-wider mb-1">Name / Caption</div>
                                        <div className="text-slate-900 dark:text-white font-medium truncate" title={biosData.name}>{biosData.name || "-"}</div>
                                    </div>
                                )}
                                <div className="bg-white dark:bg-gray-800/50 p-2.5 rounded border border-slate-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                                    <div className="text-slate-500 dark:text-gray-400 text-[10px] uppercase tracking-wider mb-1">SMBIOS Version</div>
                                    <div className="text-slate-900 dark:text-white font-mono">{biosData.smbios_bios_version || "-"}</div>
                                </div>
                                <div className="bg-white dark:bg-gray-800/50 p-2.5 rounded border border-slate-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                                    <div className="text-slate-500 dark:text-gray-400 text-[10px] uppercase tracking-wider mb-1">Release Date</div>
                                    <div className="text-slate-900 dark:text-white font-mono">{formatBiosDate(biosData.release_date)}</div>
                                </div>
                                {!isLinux && (
                                    <>
                                        <div className="bg-white dark:bg-gray-800/50 p-2.5 rounded border border-slate-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                                            <div className="text-slate-500 dark:text-gray-400 text-[10px] uppercase tracking-wider mb-1">Sys. BIOS Major/Minor</div>
                                            <div className="text-slate-900 dark:text-white font-mono">
                                                {biosData.system_bios_major_version && biosData.system_bios_minor_version
                                                    ? `${biosData.system_bios_major_version}.${biosData.system_bios_minor_version}`
                                                    : "-"}
                                            </div>
                                        </div>
                                        <div className="bg-white dark:bg-gray-800/50 p-2.5 rounded border border-slate-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                                            <div className="text-slate-500 dark:text-gray-400 text-[10px] uppercase tracking-wider mb-1">Embedded Controller</div>
                                            <div className="text-slate-900 dark:text-white font-mono">
                                                {biosData.embedded_controller_major_version && biosData.embedded_controller_minor_version
                                                    ? `${biosData.embedded_controller_major_version}.${biosData.embedded_controller_minor_version}`
                                                    : "-"}
                                            </div>
                                        </div>
                                        <div className="bg-white dark:bg-gray-800/50 p-2.5 rounded border border-slate-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                                            <div className="text-slate-500 dark:text-gray-400 text-[10px] uppercase tracking-wider mb-1">Primary BIOS</div>
                                            <div className="text-slate-900 dark:text-white">
                                                {biosData.primary_bios === "true" || biosData.primary_bios === true || biosData.primary_bios === "True" ? (
                                                    <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Yes</span>
                                                ) : biosData.primary_bios === "false" || biosData.primary_bios === false || biosData.primary_bios === "False" ? (
                                                    <span className="text-slate-400 dark:text-gray-400">No</span>
                                                ) : "-"}
                                            </div>
                                        </div>
                                        <div className="bg-white dark:bg-gray-800/50 p-2.5 rounded border border-slate-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                                            <div className="text-slate-500 dark:text-gray-400 text-[10px] uppercase tracking-wider mb-1">Status</div>
                                            <div className="text-slate-900 dark:text-white">
                                                {biosData.status === "OK" ? (
                                                    <span className="text-emerald-600 dark:text-emerald-400 font-medium">OK</span>
                                                ) : (
                                                    <span className="text-yellow-600 dark:text-yellow-400 font-medium">{biosData.status || "-"}</span>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* TPM Details Section (Windows Only) */}
                {platform?.toLowerCase() === "windows" && !tpmLoading && tpmData && (
                    <div className="bg-slate-50/70 dark:bg-gray-900 rounded-md mt-4 border border-slate-200 dark:border-gray-700/30">
                        <div className="p-3">
                            <h3 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center mb-3">
                                <Shield className="w-4 h-4 mr-1.5 text-orange-500" />
                                Trusted Platform Module (TPM)
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                                <div className="bg-white dark:bg-gray-800/50 p-2.5 rounded border border-slate-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                                    <div className="text-slate-500 dark:text-gray-400 text-[10px] uppercase tracking-wider mb-1">Manufacturer</div>
                                    <div className="text-slate-900 dark:text-white font-medium truncate" title={tpmData.manufacturer_name}>{tpmData.manufacturer_name || "-"}</div>
                                </div>
                                <div className="bg-white dark:bg-gray-800/50 p-2.5 rounded border border-slate-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                                    <div className="text-slate-500 dark:text-gray-400 text-[10px] uppercase tracking-wider mb-1">Product Name</div>
                                    <div className="text-slate-900 dark:text-white font-medium truncate" title={tpmData.product_name}>{tpmData.product_name?.trim() || "-"}</div>
                                </div>
                                <div className="bg-white dark:bg-gray-800/50 p-2.5 rounded border border-slate-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                                    <div className="text-slate-500 dark:text-gray-400 text-[10px] uppercase tracking-wider mb-1">Spec Version</div>
                                    <div className="text-slate-900 dark:text-white font-mono">{tpmData.spec_version || "-"}</div>
                                </div>
                                <div className="bg-white dark:bg-gray-800/50 p-2.5 rounded border border-slate-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                                    <div className="text-slate-500 dark:text-gray-400 text-[10px] uppercase tracking-wider mb-1">Secure Boot</div>
                                    <div className="text-slate-900 dark:text-white">
                                        {tpmData.secure_boot === true || tpmData.secure_boot === "true" ? (
                                            <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Enabled</span>
                                        ) : (
                                            <span className="text-slate-400 dark:text-gray-400 flex items-center gap-1"><XCircle className="w-3 h-3" /> Disabled</span>
                                        )}
                                    </div>
                                </div>
                                <div className="bg-white dark:bg-gray-800/50 p-2.5 rounded border border-slate-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                                    <div className="text-slate-500 dark:text-gray-400 text-[10px] uppercase tracking-wider mb-1">Status</div>
                                    <div className="text-slate-900 dark:text-white flex items-center gap-2">
                                        {tpmData.enabled ? (
                                            <span className="text-[10px] bg-emerald-50 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-transparent px-1.5 py-0.5 rounded">Enabled</span>
                                        ) : (
                                            <span className="text-[10px] bg-slate-100 dark:bg-gray-600/30 text-slate-500 dark:text-gray-400 border border-slate-200 dark:border-transparent px-1.5 py-0.5 rounded">Disabled</span>
                                        )}
                                        {tpmData.activated ? (
                                            <span className="text-[10px] bg-blue-50 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-transparent px-1.5 py-0.5 rounded">Activated</span>
                                        ) : (
                                            <span className="text-[10px] bg-slate-100 dark:bg-gray-600/30 text-slate-500 dark:text-gray-400 border border-slate-200 dark:border-transparent px-1.5 py-0.5 rounded">Deactivated</span>
                                        )}
                                    </div>
                                </div>
                                <div className="bg-white dark:bg-gray-800/50 p-2.5 rounded border border-slate-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                                    <div className="text-slate-500 dark:text-gray-400 text-[10px] uppercase tracking-wider mb-1">Ownership</div>
                                    <div className="text-slate-900 dark:text-white">
                                        {tpmData.owned === true || tpmData.owned === "true" ? (
                                            <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1"><Lock className="w-3 h-3" /> Owned</span>
                                        ) : (
                                            <span className="text-slate-400 dark:text-gray-400">Unowned</span>
                                        )}
                                    </div>
                                </div>
                                <div className="bg-white dark:bg-gray-800/50 p-2.5 rounded border border-slate-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                                    <div className="text-slate-500 dark:text-gray-400 text-[10px] uppercase tracking-wider mb-1">Manufacturer Version</div>
                                    <div className="text-slate-900 dark:text-white font-mono">{tpmData.manufacturer_version || "-"}</div>
                                </div>
                                <div className="bg-white dark:bg-gray-800/50 p-2.5 rounded border border-slate-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                                    <div className="text-slate-500 dark:text-gray-400 text-[10px] uppercase tracking-wider mb-1">Physical Presence</div>
                                    <div className="text-slate-900 dark:text-white font-mono">{tpmData.physical_presence_version || "-"}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </div>

            {/* Reboot Required Reasons Modal */}
            {showRebootModal && (
                <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-gray-900 border border-red-500/20 rounded-xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col scale-in-center">
                        <div className="p-5 border-b border-slate-200 dark:border-gray-800 bg-slate-50 dark:bg-gray-900/50 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Info className="w-5 h-5 text-red-500 animate-pulse" />
                                <h3 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider">Pending Reboot Reasons</h3>
                            </div>
                            <button
                                onClick={() => setShowRebootModal(false)}
                                className="p-1 text-slate-400 hover:text-slate-700 dark:text-gray-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-gray-800 rounded-md transition-all cursor-pointer"
                            >
                                <XCircle className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-5 overflow-y-auto max-h-[60vh] space-y-4">
                            <p className="text-xs text-slate-600 dark:text-gray-400 leading-relaxed">
                                The system flagged a reboot requirement for the following reason(s):
                            </p>
                            <div className="space-y-3">
                                {Object.keys(reasons).length === 0 ? (
                                    <div className="text-xs text-slate-400 dark:text-gray-500 italic text-center py-4">
                                        No specific reboot reasons reported.
                                    </div>
                                ) : (
                                    Object.entries(reasons).map(([key, description]) => (
                                        <div key={key} className="bg-slate-50 dark:bg-gray-950/40 border border-slate-200 dark:border-gray-800 rounded-lg p-3 space-y-1">
                                            <div className="text-[10px] font-black uppercase text-red-600 dark:text-red-400 tracking-wider">
                                                {key.replace(/_/g, ' ')}
                                            </div>
                                            <div className="text-xs text-slate-700 dark:text-gray-200 leading-relaxed">
                                                {description}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                        <div className="px-5 py-3 border-t border-slate-200 dark:border-gray-800 bg-slate-50 dark:bg-gray-900/50 flex justify-end">
                            <button
                                type="button"
                                onClick={() => setShowRebootModal(false)}
                                className="px-5 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-gray-800 dark:hover:bg-gray-700 border border-slate-200 dark:border-gray-700 text-slate-700 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white rounded-lg text-xs font-bold transition-all uppercase tracking-wider cursor-pointer"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Overview;